import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

const AddFunderDialog = ({
  open,
  onHandleOpen,
  onSaveSuccess,
  onOptimisticSave,
  onSaveError,
  hasTrigger = true,
  data = null,
}) => {
  const { handleCompressImage } = useImageCompressor();
  const postMultipart = usePostMultipart();
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [name, setName] = useState(data?.name || "");
  const [nif, setNif] = useState("");
  const [siret, setSiret] = useState("");
  const [repFirstName, setRepFirstName] = useState("");
  const [repLastName, setRepLastName] = useState("");
  const [repEmail, setRepEmail] = useState("");
  const [loadingSave, setLoadingSave] = useState(false);
  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedFinancer, setSelectedFinancer] = useState(null);

  const [querySiret, setQuerySiret] = useState("");
  const [resultsSiret, setResultsSiret] = useState([]);
  const [loadingSearchSiret, setLoadingSearchSiret] = useState(false);

  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });

  useEffect(() => {
    setName(data?.name || "");
    setNif(data?.nif || "");
    setSiret(data?.siret || "");
    setRepFirstName(data?.repFirstName || "");
    setRepLastName(data?.repLastName || "");
    setRepEmail(data?.repEmail || "");
    setPhoto(data?.photo || null);
    setSelectedFinancer(null);

    if (data?.name !== null && data?.name !== undefined && data?.name !== "") {
      setQuery(data?.name);
    }
  }, [data, open]);

  useEffect(() => {
    setLoadingSave(false);
  }, [data, open]);

  const isValidSIRET = (siret) => {
    if (!/^[0-9]{14}$/.test(siret)) {
      return false;
    }

    return true;
  };
  const handlePhotoChange = async (value) => {
    if (value) {
      setUploadPhotoLoading(true);

      // Compresser l'image avant l'upload
      const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);

      // Si l'image est compressée, elle est disponible dans `compressedImageBlob`
      const data = new FormData();
      data.append("type", 3);
      const fileDefaultName = value[0].name;

      if (compressedBlob) {
        for (let i = 0; i < value.length; i++) {
          const renamedFile = new File(
            [compressedBlob], // contenu
            fileDefaultName, // nouveau nom
            { type: compressedBlob.type } // garder le type MIME d'origine
          );
          data.append("files[]", renamedFile);
        }
      } else {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", value[0]);
        }
      }
      const newMedias = await postMultipart.postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });
      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({
          title: newMedias.message,
          duration: 1000,
        });
        setUploadPhotoLoading(false);
        return;
      }

      setPhoto(newMedias[0]);
      setUploadPhotoLoading(false);
      return;
    }
  };

  const handleSave = async () => {
    //check if all fields are required
    if (name === "") {
      toast({
        title: t("Veuillez remplir les champs obligatoires"),
        duration: 1000,
      });
      return;
    }

    // if (repEmail === "") {
    //   toast({
    //     title: t("Veuillez entrer l'email"),
    //     duration: 1000,
    //   });
    //   return;
    // }

    if (siret === "" && nif === "") {
      toast({
        title: t("Veuillez entrer le siret ou le nif"),
        duration: 1000,
      });
      return;
    }

    if (siret !== "" && !isValidSIRET(siret)) {
      toast({
        title: t("Le SIRET est incorrect"),
        duration: 1000,
      });
      return;
    }

    setLoadingSave(true);
    const optimisticFunder = {
      id: `optimistic-funder-${Date.now()}`,
      structureName: name,
      siret,
      nif,
      photo,
      access: false,
      representants:
        repFirstName || repLastName || repEmail
          ? [
              {
                firstname: repFirstName,
                lastname: repLastName,
                email: repEmail,
              },
            ]
          : [],
    };
    const optimisticMeta = onOptimisticSave?.(optimisticFunder);

    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/financer`,
      data: {
        structureName: name,
        siret,
        nif,
        firstname: repFirstName,
        lastname: repLastName,
        email: repEmail,
        photo: photo?.id,
        selectedFinancer,
      },
    });

    if (res) {
      toast({
        title: t("Le financeur a bien été ajouté."),
        duration: 1000,
      });
      onSaveSuccess(res, optimisticMeta);
      setLoadingSave(false);
      return;
    }

    onSaveError?.(optimisticMeta);
    toast({
      title: t("Une erreur est survenue, le financeur n'a pas pu être ajouté."),
      duration: 1000,
    });
    setLoadingSave(false);
  };

  useEffect(() => {
    console.log("photo", photo);
  }, [photo]);
  const fetchResults = async (type = 1) => {
    if (type === 1) {
      setLoadingSearch(true);
    } else {
      setLoadingSearchSiret(true);
    }
    let token = localStorage.getItem("jwtToken");
    if (token === null) {
      token = sessionStorage.getItem("jwtToken");
    }
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/financer/search/external?s=${
          type === 1 ? query : querySiret
        }&type=${type}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (type === 1) {
        setResults(data.list || []);
      } else {
        setResultsSiret(data.list || []);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des résultats :", error);
      if (type === 1) {
        setResults([]);
      } else {
        setResultsSiret([]);
      }
    } finally {
      if (type === 1) {
        setLoadingSearch(false);
      } else {
        setLoadingSearchSiret(false);
      }
    }
  };

  useEffect(() => {
    if (query === undefined || query.length < 3) {
      setResults([]);
      return;
    }

    if (query.length > 0) {
      setSelectedFinancer(null);
    }

    const delayDebounceFn = setTimeout(() => {
      fetchResults(1);
    }, 300); // Attente de 300ms pour éviter les appels API excessifs

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  useEffect(() => {
    if (querySiret.length < 3) {
      setResultsSiret([]);
      return;
    }

    if (querySiret.length > 0) {
      setSelectedFinancer(null);
    }

    const delayDebounceFn = setTimeout(() => {
      fetchResults(2);
    }, 300); // Attente de 300ms pour éviter les appels API excessifs

    return () => clearTimeout(delayDebounceFn);
  }, [querySiret]);

  if (hidePostActions) return null;
  return (
    <Dialog open={open} onOpenChange={onHandleOpen}>
      {hasTrigger && (
        <DialogTrigger asChild>
          <Button
            id="add_funder"
            className={buttonVariants({ variant: "default" })}
          >
            {t("Ajouter un financeur")}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto">
        <DialogHeader className="">
          <DialogTitle asChild>
            <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
              {t("Ajouter un financeur")}{" "}
            </h2>
          </DialogTitle>
          <DialogDescription asChild className="text-[#595959]">
            <p className="">
              {t(
                "Chaque financeur contribue au soutien des projets avec des modalités spécifiques. L'ajout d'un financeur permet de structurer les financements, d'assurer leur traçabilité et de suivre leur impact."
              )}
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap mt-5 h-full overflow-y-auto">
          <div className="basis-1/3 pr-4">
            <FileUploader
              value={[]}
              onValueChange={handlePhotoChange}
              dropzoneOptions={{
                multiple: false,
              }}
              className={`relative min-h-[200px] rounded-lg bg-background`}
            >
              <FileInput className="border-indigo-foreground size-full border-2 border-dashed">
                {!uploadPhotoLoading && photo === null && (
                  <div className="flex size-full flex-col items-center justify-center pb-4 pt-3">
                    <svg
                      className="size-8 text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    {t("Photo")}
                  </div>
                )}
                {!uploadPhotoLoading && photo !== null && (
                  <div className="flex size-full flex-col items-center justify-center">
                    <img
                      src={import.meta.env.VITE_MEDIA_BASE + photo.uri}
                      alt=""
                      className="size-full object-cover"
                    />
                  </div>
                )}
                {uploadPhotoLoading && (
                  <div
                    className={`w-35 relative mr-2 flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-input bg-gray-200`}
                  >
                    <div className="customfileuploaderloader">
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                      <div className="block"></div>
                    </div>
                  </div>
                )}
              </FileInput>
            </FileUploader>
          </div>
          <div className="basis-2/3">
            <div className="flex w-full flex-wrap">
              <label
                className=" mb-1 w-full font-medium text-[#2D3720]"
                htmlFor=""
              >
                {t("Nom")}*
              </label>
              <div className="w-full">
                <Input
                  type="text"
                  style={{
                    color: "#878787",
                  }}
                  onChange={(e) => {
                    setName(e.target.value);
                    setQuery(e.target.value);
                  }}
                  className="text-muted-foreground"
                  value={name}
                />
                {loadingSearch && (
                  <p className="absolute mt-2 text-sm rounded-md border border-gray-300 bg-white shadow-lg px-4 py-2 text-gray-500">
                    Chargement...
                  </p>
                )}
                {!loadingSearch && query && results.length > 0 && (
                  <ul className="absolute mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    {results.map((result, index) => (
                      <li
                        key={index}
                        className="flex cursor-pointer gap-2 border-b px-4 py-2 last:border-b-0 hover:bg-blue-100"
                        onClick={() => {
                          setSelectedFinancer(result.id);
                          setName(result.nom_entreprise);
                          setSiret(
                            result.siege
                              ? result.siege.siret
                                ? result.siege.siret
                                : null
                              : null
                          );
                          setNif(
                            result.numero_tva_intracommunautaire
                              ? result.numero_tva_intracommunautaire
                              : null
                          );
                          // setPhoto(null);
                          setQuery("");
                          setResults([]);
                        }}
                      >
                        {!result.photo && <div className="size-6"></div>}
                        {result.photo && (
                          <div className="size-6">
                            <img
                              src={
                                result.photo
                                  ? import.meta.env.VITE_MEDIA_BASE +
                                    result.photo.uri
                                  : ""
                              }
                              alt={result.structureName}
                              height={40}
                              className="size-full object-cover"
                            />
                          </div>
                        )}
                        <span>{result.nom_entreprise}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="mt-4 flex w-full flex-wrap">
              <label
                className=" mb-1 w-full font-medium text-[#2D3720]"
                htmlFor=""
              >
                {t("Siret")}{" "}
                <span className="text-[12px] text-muted-foreground">
                  (
                  {t(
                    "A remplir pour toute organisation, entreprise, collectivité ou association basée en France."
                  )}
                  )
                </span>
              </label>
              <div className="w-full">
                <Input
                  type="number"
                  style={{
                    color: "#878787",
                  }}
                  onChange={(e) => {
                    setSiret(e.target.value);
                    setQuerySiret(e.target.value);
                  }}
                  className="text-muted-foreground"
                  value={siret}
                />
                {loadingSearchSiret && (
                  <p className="absolute mt-2 text-sm rounded-md border border-gray-300 bg-white shadow-lg px-4 py-2 text-gray-500">
                    Chargement...
                  </p>
                )}
                {!loadingSearchSiret &&
                  querySiret &&
                  resultsSiret.length > 0 && (
                    <ul className="absolute mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                      {resultsSiret.map((result, index) => (
                        <li
                          key={index}
                          className="flex cursor-pointer gap-2 border-b px-4 py-2 last:border-b-0 hover:bg-blue-100"
                          onClick={() => {
                            setSelectedFinancer(result.id);
                            setName(result.nom_entreprise);
                            setSiret(
                              result.siege
                                ? result.siege.siret
                                  ? result.siege.siret
                                  : null
                                : null
                            );
                            setNif(
                              result.numero_tva_intracommunautaire
                                ? result.numero_tva_intracommunautaire
                                : null
                            );
                            // setPhoto(null);
                            setQuerySiret("");
                            setResultsSiret([]);
                          }}
                        >
                          {!result.photo && <div className="size-6"></div>}
                          {result.photo && (
                            <div className="size-6">
                              <img
                                src={
                                  result.photo
                                    ? import.meta.env.VITE_MEDIA_BASE +
                                      result.photo.uri
                                    : ""
                                }
                                alt={result.structureName}
                                height={40}
                                className="size-full object-cover"
                              />
                            </div>
                          )}
                          <span>{result.nom_entreprise}</span>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>
            <div className="mt-4 flex w-full flex-wrap">
              <label
                className=" mb-1 w-full font-medium text-[#2D3720]"
                htmlFor=""
              >
                {t("NIF/STAT")}{" "}
                <span className="text-[12px] text-muted-foreground">
                  ({t("ou Numéro de TVA")})
                </span>
              </label>
              <Input
                type="text"
                style={{
                  color: "#878787",
                }}
                onChange={(e) => {
                  setNif(e.target.value);
                }}
                className="text-muted-foreground"
                value={nif}
              />
            </div>
            <div className="mt-8 w-full" hidden>
              <h3 className=" mb-2 text-sm font-medium text-[#2D3720]">
                {t("Représentant")}
              </h3>
              <div className="flex flex-wrap items-start">
                <div className="flex w-full flex-wrap items-start">
                  <div className="mb-4 basis-1/2 pr-2">
                    <label className="mb-2 block">{t("NPrenom")}</label>
                    <Input
                      className="w-full text-muted-foreground"
                      value={repFirstName}
                      onChange={(e) => {
                        setRepFirstName(e.target.value);
                      }}
                    />
                  </div>
                  <div className="mb-4 basis-1/2 pl-2">
                    <label className="mb-2 block">{t("Prenom")}</label>
                    <Input
                      className="w-full text-muted-foreground"
                      value={repLastName}
                      onChange={(e) => {
                        setRepLastName(e.target.value);
                      }}
                    />
                  </div>
                  <div className="mb-4 basis-full">
                    <label className="mb-2 block">{t("Email")}</label>
                    <Input
                      className="w-full text-muted-foreground"
                      value={repEmail}
                      onChange={(e) => {
                        setRepEmail(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex justify-end mt-5">
            <DialogFooter className="pb-5">
              <Button
                onClick={() => handleSave()}
                type="button"
                disabled={loadingSave}
              >
                {loadingSave ? "..." : t("Sauvegarder")}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default AddFunderDialog;
